/**
 * Account Deletion Service
 * 
 * Handles permanent deletion of user accounts and all associated data.
 * This service calls a Cloud Function that deletes:
 * - User profile and subcollections
 * - All trips owned by user
 * - User membership in collaborative trips
 * - All trip subcollections (bookings, expenses, itinerary, etc.)
 * - Storage files (documents, photos)
 * - OAuth credentials
 */

import { httpsCallableWithTimeout, functions } from '@/lib/functions';
import { auth } from '@/lib/firebase';

type DeleteAccountResponse = {
  success: boolean;
  message: string;
};

/** Match server timeout (540s) so large accounts do not fail with a client-side deadline. */
const DELETE_ACCOUNT_CALLABLE_TIMEOUT_MS = 600_000;

const deleteUserAccountCallable = httpsCallableWithTimeout<{}, DeleteAccountResponse>(
  functions,
  'deleteUserAccount',
  DELETE_ACCOUNT_CALLABLE_TIMEOUT_MS,
);

/**
 * Permanently delete the current user's account and all associated data.
 * 
 * This function:
 * 1. Calls the Cloud Function to delete all Firestore data, Storage files, and the Auth user (Admin SDK)
 * 2. Signs the user out locally
 * 
 * @throws Error if deletion fails at any step
 */
export const deleteAccount = async (): Promise<void> => {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new Error('No user is currently signed in');
  }

  try {
    // Step 1: Delete all Firestore data and Storage files via Cloud Function
    console.log('[AccountDeletion] Starting account deletion...');
    const result = await deleteUserAccountCallable({});
    
    if (!result.data.success) {
      throw new Error(result.data.message || 'Failed to delete account data');
    }

    console.log('[AccountDeletion] Cloud Function deletion successful (includes Auth user via Admin SDK)');

    // Auth user is already removed server-side. Clear local session only — client deleteUser()
    // often fails with auth/requires-recent-login and previously left orphaned Auth + no profile.
    await auth.signOut();
    
    console.log('[AccountDeletion] Account deletion completed successfully');
  } catch (error: any) {
    console.error('[AccountDeletion] Error during account deletion:', error);
    
    // Provide user-friendly error messages
    if (error?.code === 'functions/unauthenticated') {
      throw new Error('You must be signed in to delete your account. Please sign in and try again.');
    } else if (error?.code === 'functions/permission-denied') {
      throw new Error('You do not have permission to delete this account.');
    } else if (error?.code === 'auth/requires-recent-login') {
      throw new Error('For security, please sign in again before deleting your account.');
    } else if (error?.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to delete account. Please try again or contact support.');
    }
  }
};

